/**
 * Threshold alert FSM (§12.4).
 *
 *   armed → triggered          (value crosses threshold)
 *   triggered → cooldown       (toast emitted)
 *   cooldown → armed           (value < threshold for ≥ resetAfterMs)
 *   cooldown → remind          (value stays above for ≥ remindAfterMs)
 *   remind → cooldown          (remind toast emitted)
 *
 * The ±10% deadband prevents jitter at the threshold edge from re-arming
 * a noisy alert every sample.
 */

export type AlertPhase = 'armed' | 'triggered' | 'cooldown' | 'remind';

export interface AlertSpec {
  id: string;
  /** Human label rendered in the toast, e.g. 'CPU 占用 > 80%'. */
  label: string;
  /** Threshold value (units depend on metric). */
  threshold: number;
  /** Direction: alert when value > threshold (default) or < threshold. */
  direction?: 'above' | 'below';
  /**
   * Re-arm only after value stays past the threshold by this fraction.
   * Default 0.1 = ±10% deadband. Set to 0 to disable.
   */
  deadband?: number;
  /**
   * Once cooled down, value must stay on the "safe" side for at least
   * this long before transitioning back to `armed`. Default 10_000 ms.
   */
  resetAfterMs?: number;
  /**
   * Stay-above remind cadence: while in cooldown, fire a remind toast
   * every N ms if the value is still past threshold. Default 5 min.
   */
  remindAfterMs?: number;
}

export interface AlertState {
  phase: AlertPhase;
  /** When `phase` last changed. Used for `resetAfterMs` / `remindAfterMs`. */
  enteredPhaseAt: number;
  /** Last sample timestamp we saw (for ordering). */
  lastSampleAt?: number;
}

export type AlertEvent =
  | { type: 'fire'; spec: AlertSpec; value: number; t: number; reason: 'triggered' | 'remind' };

export function initAlertState(now: number): AlertState {
  return { phase: 'armed', enteredPhaseAt: now };
}

/**
 * Step the state machine with one sample value. Returns the new state and
 * (optionally) an event the caller should surface via toast.
 *
 * Pure — no side effects. Caller decides what to do with the event.
 */
export function stepAlert(
  spec: AlertSpec,
  state: AlertState,
  value: number | undefined,
  t: number
): { state: AlertState; event?: AlertEvent } {
  // Missing sample (e.g. perf.gfxinfo not available) — keep state but
  // bump lastSampleAt so cooldown / remind timers still progress.
  if (value === undefined || !Number.isFinite(value)) {
    return { state: { ...state, lastSampleAt: t } };
  }
  const direction = spec.direction ?? 'above';
  const deadband = spec.deadband ?? 0.1;
  const above = direction === 'above' ? value > spec.threshold : value < spec.threshold;
  const reArmBound =
    direction === 'above'
      ? spec.threshold * (1 - deadband)
      : spec.threshold * (1 + deadband);
  const belowReArm =
    direction === 'above' ? value < reArmBound : value > reArmBound;

  const resetAfterMs = spec.resetAfterMs ?? 10_000;
  const remindAfterMs = spec.remindAfterMs ?? 5 * 60_000;

  switch (state.phase) {
    case 'armed': {
      if (above) {
        const event: AlertEvent = { type: 'fire', spec, value, t, reason: 'triggered' };
        return {
          state: { phase: 'cooldown', enteredPhaseAt: t, lastSampleAt: t },
          event,
        };
      }
      return { state: { ...state, lastSampleAt: t } };
    }
    case 'triggered': {
      // This branch is unreachable in practice (we transition straight from
      // `armed` to `cooldown` on the same step), but include it for
      // completeness so callers can persist state mid-step.
      return { state: { phase: 'cooldown', enteredPhaseAt: t, lastSampleAt: t } };
    }
    case 'cooldown': {
      if (belowReArm) {
        if (t - state.enteredPhaseAt >= resetAfterMs) {
          return { state: { phase: 'armed', enteredPhaseAt: t, lastSampleAt: t } };
        }
        return { state: { ...state, lastSampleAt: t } };
      }
      // Still above threshold. Maybe time for a remind toast.
      if (t - state.enteredPhaseAt >= remindAfterMs) {
        const event: AlertEvent = { type: 'fire', spec, value, t, reason: 'remind' };
        return {
          state: { phase: 'cooldown', enteredPhaseAt: t, lastSampleAt: t },
          event,
        };
      }
      return { state: { ...state, lastSampleAt: t } };
    }
    case 'remind': {
      // Same as cooldown for now — the `remind` phase only exists in case
      // callers want to differentiate "first-ever toast" from "subsequent
      // reminder" in their UI. We fold them together here.
      return { state: { ...state, phase: 'cooldown' } };
    }
  }
}
